import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Button, Card, useToast } from '@caruso/ui';
import { usersService } from '@/services/users.service';
import { useAuthStore } from '@/stores/auth-store';
import { TaskExecutionModal } from '@/components/task-execution-modal';
import {
  ListTodo,
  Clock,
  Camera,
  CheckCircle,
  AlertCircle,
  Calendar,
  RefreshCw,
} from 'lucide-react';

interface ProcessTask {
  id: string;
  name: string;
  description?: string;
  order: number;
  minTime: string;
  maxTime: string;
  photoRequired: boolean;
  active: boolean;
}

interface Process {
  id: string;
  name: string;
  description?: string;
  frequency: string;
  active: boolean;
  tasks: ProcessTask[];
}

const frequencyLabels: Record<string, string> = {
  DAILY: 'Diário',
  WEEKLY: 'Semanal',
  MONTHLY: 'Mensal',
};

export function MyProcessesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [executionModal, setExecutionModal] = useState<{
    open: boolean;
    processId: string;
    processName: string;
  } | null>(null);

  // Atualizar horário a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Atualiza a cada 1 minuto

    return () => clearInterval(interval);
  }, []);

  const { data: processes, isLoading, refetch } = useQuery({
    queryKey: ['my-activities', user?.id],
    queryFn: () => usersService.getUserProcesses(user?.id!),
    enabled: !!user?.id,
  });

  // Função para verificar se está dentro do horário
  const isWithinTimeRange = (minTime: string, maxTime: string): boolean => {
    const now = currentTime;
    const currentMinutes = now.getHours() * 60 + now.getMinutes();

    const [minHour, minMinute] = minTime.split(':').map(Number);
    const minMinutes = minHour * 60 + minMinute;

    const [maxHour, maxMinute] = maxTime.split(':').map(Number);
    const maxMinutes = maxHour * 60 + maxMinute;

    return currentMinutes >= minMinutes && currentMinutes <= maxMinutes;
  };

  // Função para verificar se o processo deve ser executado hoje
  const shouldExecuteToday = (frequency: string): boolean => {
    const now = currentTime;
    const dayOfWeek = now.getDay(); // 0 = Domingo, 6 = Sábado
    const dayOfMonth = now.getDate();

    switch (frequency) {
      case 'DAILY':
        return true;
      case 'WEEKLY':
        // Executar toda segunda-feira (pode ajustar conforme necessário)
        return dayOfWeek === 1;
      case 'MONTHLY':
        // Executar no primeiro dia do mês
        return dayOfMonth === 1;
      default:
        return true;
    }
  };

  // Filtrar processos e tarefas que devem ser executados agora
  const availableProcesses = processes
    ?.filter((process: Process) => {
      return (
        process.active &&
        shouldExecuteToday(process.frequency) &&
        process.tasks.some(
          (task) => task.active && isWithinTimeRange(task.minTime, task.maxTime)
        )
      );
    })
    .map((process: Process) => ({
      ...process,
      tasks: process.tasks.filter(
        (task) => task.active && isWithinTimeRange(task.minTime, task.maxTime)
      ),
    }));

  const formatTime = (time: string) => {
    return time;
  };

  const getCurrentTimeString = () => {
    return currentTime.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleRefresh = () => {
    setCurrentTime(new Date());
    refetch();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 mx-auto mb-4 text-brand-gold animate-spin" />
          <p className="text-brand-ivory/60">Carregando processos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-serif text-brand-gold">Minhas Atividades</h1>
          <p className="text-brand-ivory/60 mt-1">
            Execute as tarefas dos processos atribuídos a você
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-brand-ivory/80">
            <Clock className="w-5 h-5 text-brand-gold" />
            <span className="font-mono text-lg">{getCurrentTimeString()}</span>
          </div>
          <Button onClick={handleRefresh} variant="outline" size="icon">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Status Card */}
      <Card className="bg-brand-ink/50 border-brand-gold/20">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-brand-gold/20">
                <ListTodo className="w-6 h-6 text-brand-gold" />
              </div>
              <div>
                <p className="text-sm text-brand-ivory/60">Processos Disponíveis</p>
                <p className="text-2xl font-bold text-brand-gold">
                  {availableProcesses?.length || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-blue-500/20">
                <CheckCircle className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-brand-ivory/60">Tarefas Disponíveis</p>
                <p className="text-2xl font-bold text-blue-400">
                  {availableProcesses?.reduce(
                    (acc: number, p: Process) => acc + p.tasks.length,
                    0
                  ) || 0}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-lg bg-green-500/20">
                <Calendar className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-brand-ivory/60">Data</p>
                <p className="text-lg font-semibold text-green-400">
                  {currentTime.toLocaleDateString('pt-BR')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Processes List */}
      {availableProcesses && availableProcesses.length > 0 ? (
        <div className="grid gap-6">
          {availableProcesses.map((process: Process) => (
            <motion.div
              key={process.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <Card className="bg-brand-ink/50 border-brand-gold/20">
                <div className="p-6">
                  {/* Process Header */}
                  <div className="mb-6">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <ListTodo className="w-6 h-6 text-brand-gold" />
                          <h2 className="text-2xl font-serif text-brand-gold">
                            {process.name}
                          </h2>
                        </div>
                        {process.description && (
                          <p className="text-brand-ivory/60 mb-3">{process.description}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-gold/20 text-brand-gold border border-brand-gold/30">
                            {frequencyLabels[process.frequency] || process.frequency}
                          </span>
                          <span className="text-sm text-brand-ivory/60">
                            {process.tasks.length} {process.tasks.length === 1 ? 'tarefa' : 'tarefas'} disponível
                            {process.tasks.length !== 1 ? 'is' : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Tasks List */}
                  <div className="space-y-3">
                    <h3 className="text-sm font-semibold text-brand-gold uppercase tracking-wide mb-3">
                      Tarefas para Executar Agora
                    </h3>
                    {process.tasks.map((task, index) => (
                      <motion.div
                        key={task.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 bg-brand-smoke/40 rounded-lg border border-brand-gold/10 hover:border-brand-gold/30 transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center">
                              <span className="text-brand-gold font-bold">
                                {task.order + 1}
                              </span>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-lg font-medium text-brand-ivory mb-1">
                              {task.name}
                            </h4>
                            {task.description && (
                              <p className="text-sm text-brand-ivory/60 mb-3">
                                {task.description}
                              </p>
                            )}
                            <div className="flex flex-wrap items-center gap-3 text-sm">
                              <div className="flex items-center gap-1.5 text-brand-gold">
                                <Clock className="w-4 h-4" />
                                <span>
                                  {formatTime(task.minTime)} - {formatTime(task.maxTime)}
                                </span>
                              </div>
                              {task.photoRequired && (
                                <div className="flex items-center gap-1.5 text-blue-400">
                                  <Camera className="w-4 h-4" />
                                  <span>Foto obrigatória</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <Button
                              variant="outline"
                              className="whitespace-nowrap"
                              onClick={() => {
                                setExecutionModal({
                                  open: true,
                                  processId: process.id,
                                  processName: process.name,
                                });
                              }}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Executar
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <Card className="bg-brand-ink/50 border-brand-gold/20">
          <div className="p-12 text-center">
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-brand-gold/30" />
            <h3 className="text-xl font-serif text-brand-gold mb-2">
              Nenhuma tarefa disponível no momento
            </h3>
            <p className="text-brand-ivory/60 mb-4">
              Não há processos ou tarefas que precisam ser executados neste horário.
            </p>
            <p className="text-sm text-brand-ivory/40">
              As tarefas aparecerão aqui automaticamente quando estiverem dentro do horário
              definido.
            </p>
          </div>
        </Card>
      )}

      {/* Task Execution Modal */}
      {executionModal && (
        <TaskExecutionModal
          open={executionModal.open}
          onOpenChange={(open) => {
            if (!open) {
              setExecutionModal(null);
            }
          }}
          processId={executionModal.processId}
          processName={executionModal.processName}
        />
      )}
    </div>
  );
}
